import {
  type Instrument,
  INSTRUMENT_DIRECTION_LABELS,
  INSTRUMENT_KIND_LABELS,
  INSTRUMENT_STATUS_GROUP_LABELS,
  INSTRUMENT_STATUS_GROUPS,
  instrumentDirectionSchema,
  instrumentKindSchema,
  type InstrumentStatusGroup,
  instrumentStatusGroupSchema,
} from "@station/contracts";
import { Money } from "@station/core";
import {
  Alert,
  Button,
  DataTable,
  DateInput,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FilePlus2, Printer, ScrollText, X } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { useInstrumentColumns } from "../../../features/instruments/columns.js";
import { applicableBatchTypes, selectionTotal } from "../../../features/instruments/instrument-values.js";
import {
  BatchActionsMenu,
  InstrumentPage,
  useInstrumentPermissions,
} from "../../../features/instruments/shared.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";

const GROUPS = Object.keys(INSTRUMENT_STATUS_GROUPS) as InstrumentStatusGroup[];
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: instrumentStatusGroupSchema.optional().catch(undefined),
  direction: instrumentDirectionSchema.optional().catch(undefined),
  kind: instrumentKindSchema.optional().catch(undefined),
  dueFrom: date,
  dueTo: date,
  bankAccountId: z.string().uuid().optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
});
type PortfolioSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/cek-senet/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PortfolioPage,
});

function PortfolioPage() {
  const { t } = useTranslation();
  const permissions = useInstrumentPermissions();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tab = search.tab ?? "PORTFOLIO";
  return (
    <InstrumentPage
      title={t("menu.instrumentPortfolio")}
      description={t("instruments.list.description")}
      actions={
        permissions.canChange ? (
          <Button asChild>
            <Link to="/cek-senet/bordro-yeni">
              <FilePlus2 aria-hidden="true" />
              {t("menu.instrumentNewBatch")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              // Sekme değişince süzgeçler korunur; seçim ve sayfa sıfırlanır.
              search: (previous) => ({ ...previous, tab: next as InstrumentStatusGroup }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("instruments.list.tabs")} className="flex-wrap">
            {GROUPS.map((group) => (
              <TabsTrigger key={group} value={group}>
                {INSTRUMENT_STATUS_GROUP_LABELS[group]}
              </TabsTrigger>
            ))}
          </TabsList>
          {GROUPS.map((group) => (
            <TabsContent key={group} value={group}>
              {tab === group ? (
                <PortfolioList key={group} companyId={companyId} group={group} search={search} />
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </InstrumentPage>
  );
}

function PortfolioList({
  companyId,
  group,
  search,
}: {
  companyId: string;
  group: InstrumentStatusGroup;
  search: PortfolioSearch;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const accounts = useBankAccounts(companyId);
  const [searchText, setSearchText] = useState(search.q ?? "");
  const [selected, setSelected] = useState<Map<string, Instrument>>(new Map());
  const [printRows, setPrintRows] = useState<Instrument[] | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const setFilters = (patch: Partial<PortfolioSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  // Yazdırma görünümü: tüm sayfalar çekilip yalnızca baskıda görünen tabloya yazılır, sonra tarayıcı yazdır açılır.
  useEffect(() => {
    if (!printRows) return;
    const done = () => setPrintRows(null);
    window.addEventListener("afterprint", done, { once: true });
    window.print();
    return () => window.removeEventListener("afterprint", done);
  }, [printRows]);

  const query = {
    companyId,
    limit: 25,
    group,
    ...(search.direction ? { direction: search.direction } : {}),
    ...(search.kind ? { kind: search.kind } : {}),
    ...(search.dueFrom ? { dueFrom: search.dueFrom } : {}),
    ...(search.dueTo ? { dueTo: search.dueTo } : {}),
    ...(search.bankAccountId ? { bankAccountId: search.bankAccountId } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
    ...(search.q ? { search: search.q } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.instruments(companyId, "list", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/instruments", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const selection = {
    isSelected: (id: string) => selected.has(id),
    toggle: (instrument: Instrument, on: boolean) =>
      setSelected((current) => {
        const next = new Map(current);
        if (on) next.set(instrument.id, instrument);
        else next.delete(instrument.id);
        return next;
      }),
  };
  const columns = useInstrumentColumns({ selection });
  const rows = list.data?.items ?? [];
  const selectedList = [...selected.values()];
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(row.id));

  async function fetchAll(): Promise<Instrument[]> {
    return fetchAllPages(
      async (cursor) =>
        (
          await api.GET("/instruments", {
            params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
          })
        ).data!,
    );
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 print:hidden">
        {list.data ? (
          <p className="text-sm" aria-live="polite">
            {t("instruments.list.totals", { count: list.data.totalCount })}{" "}
            <MoneyText value={list.data.totalAmount} className="font-semibold" />
          </p>
        ) : null}

        {selectedList.length > 0 ? (
          <div
            className="flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
            role="region"
            aria-label={t("instruments.list.selectionRegion")}
          >
            <span className="font-medium">
              {t("instruments.list.selected", { count: selectedList.length })}{" "}
              <MoneyText value={selectionTotal(selectedList).toString()} className="font-semibold" />
            </span>
            <BatchActionsMenu
              size="sm"
              label={t("instruments.list.createBatch")}
              types={applicableBatchTypes(selectedList)}
              instrumentIds={selectedList.map((item) => item.id)}
            />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Map())}>
              <X aria-hidden="true" />
              {t("instruments.list.clearSelection")}
            </Button>
          </div>
        ) : null}

        <DataTable
          caption={t("menu.instrumentPortfolio")}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
          emptyState={
            <EmptyState
              headingLevel={2}
              icon={<ScrollText />}
              title={t("instruments.list.emptyTitle")}
              description={t("instruments.list.emptyDescription")}
            />
          }
          filters={
            <>
              <FormField label={t("instruments.fields.direction")} className="w-44">
                {(control) => (
                  <Select
                    {...control}
                    value={search.direction ?? ""}
                    onChange={(event) =>
                      setFilters({
                        direction:
                          event.target.value === ""
                            ? undefined
                            : instrumentDirectionSchema.parse(event.target.value),
                      })
                    }
                  >
                    <option value="">{t("common.all")}</option>
                    {instrumentDirectionSchema.options.map((direction) => (
                      <option key={direction} value={direction}>
                        {INSTRUMENT_DIRECTION_LABELS[direction]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("instruments.fields.kind")} className="w-32">
                {(control) => (
                  <Select
                    {...control}
                    value={search.kind ?? ""}
                    onChange={(event) =>
                      setFilters({
                        kind:
                          event.target.value === ""
                            ? undefined
                            : instrumentKindSchema.parse(event.target.value),
                      })
                    }
                  >
                    <option value="">{t("common.all")}</option>
                    {instrumentKindSchema.options.map((kind) => (
                      <option key={kind} value={kind}>
                        {INSTRUMENT_KIND_LABELS[kind]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("instruments.list.dueFrom")} className="w-40">
                {(control) => (
                  <DateInput
                    {...control}
                    value={search.dueFrom ?? null}
                    onValueChange={(value) => setFilters({ dueFrom: value ?? undefined })}
                  />
                )}
              </FormField>
              <FormField label={t("instruments.list.dueTo")} className="w-40">
                {(control) => (
                  <DateInput
                    {...control}
                    value={search.dueTo ?? null}
                    onValueChange={(value) => setFilters({ dueTo: value ?? undefined })}
                  />
                )}
              </FormField>
              <FormField label={t("instruments.fields.bankAccount")} className="w-56">
                {(control) => (
                  <Select
                    {...control}
                    value={search.bankAccountId ?? ""}
                    onChange={(event) => setFilters({ bankAccountId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(accounts.data ?? []).map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.bankName} · {account.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("instruments.fields.counterparty")} className="w-64">
                {(control) => (
                  <CounterpartyIdCombobox
                    {...control}
                    companyId={companyId}
                    counterpartyId={search.counterpartyId ?? null}
                    onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
                  />
                )}
              </FormField>
              <FormField label={t("common.search")} className="w-56">
                {(control) => (
                  <Input
                    {...control}
                    type="search"
                    value={searchText}
                    placeholder={t("instruments.list.searchPlaceholder")}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                )}
              </FormField>
            </>
          }
          toolbar={
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={rows.length === 0}
                onClick={() =>
                  setSelected((current) => {
                    const next = new Map(current);
                    for (const row of rows) {
                      if (allOnPage) next.delete(row.id);
                      else next.set(row.id, row);
                    }
                    return next;
                  })
                }
              >
                {allOnPage ? t("instruments.list.unselectPage") : t("instruments.list.selectPage")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={rows.length === 0}
                loading={printing}
                onClick={() => {
                  setPrinting(true);
                  setPrintError(null);
                  fetchAll()
                    .then(setPrintRows)
                    .catch((error: unknown) => setPrintError(errorMessage(error)))
                    .finally(() => setPrinting(false));
                }}
              >
                <Printer aria-hidden="true" />
                {t("instruments.list.print")}
              </Button>
            </>
          }
          pagination={{
            hasPrevious: pages.hasPrevious,
            hasNext: list.data?.page.hasMore ?? false,
            onPrevious: pages.previous,
            onNext: () => pages.next(list.data?.page.nextCursor),
            pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
          }}
          onExport={async (visible) => {
            await exportRowsToExcel({
              fileName: "cek-senet-portfoy",
              sheetName: INSTRUMENT_STATUS_GROUP_LABELS[group],
              columns: visible,
              rows: await fetchAll(),
            });
          }}
          onRowClick={(row) => void navigate({ to: "/cek-senet/$id", params: { id: row.id } })}
        />
        {printError ? <Alert tone="danger">{printError}</Alert> : null}
      </div>

      {printRows ? (
        <div className="hidden print:block">
          <p className="mb-2 text-sm">
            {INSTRUMENT_STATUS_GROUP_LABELS[group]} ·{" "}
            {t("instruments.list.totals", { count: list.data?.totalCount ?? printRows.length })}{" "}
            {Money.fromMinor(list.data?.totalAmount ?? "0").formatTr()}
          </p>
          <SimpleTable
            caption={t("menu.instrumentPortfolio")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.portfolioNo")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.serialNo")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.drawer")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.counterparty")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.location")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.dueDate")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("instruments.fields.remaining")}
                </th>
              </tr>
            }
          >
            {printRows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className={tdClass}>{row.portfolioNo}</td>
                <td className={tdClass}>{row.serialNo ?? "—"}</td>
                <td className={tdClass}>{row.drawerName}</td>
                <td className={tdClass}>{row.counterpartyName}</td>
                <td className={tdClass}>{row.locationLabel}</td>
                <td className={tdClass}>{formatDate(row.dueDate)}</td>
                <td className={tdRightClass}>
                  {Money.fromMinor(row.remainingAmount).formatTr({ symbol: false })}
                </td>
              </tr>
            ))}
          </SimpleTable>
        </div>
      ) : null}
    </div>
  );
}
